import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { ConfirmationService, MenuItem, MessageService, SortEvent } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Paginator } from 'primeng/paginator';
import { map, Subscription } from 'rxjs';
import { IContact } from 'src/app/common/model/contacts.model';
import { IOrganization } from 'src/app/common/model/organization.model';
import { IUser } from 'src/app/common/model/user.model';
import { IPagination } from 'src/app/common/model/util.model';
import { DeleteConfirmDialogComponent } from 'src/app/common/modules/shared/components/delete-confirm-dialog/deleteConfirmDialog.component';
import { OrganizationService } from 'src/app/common/services/organization/organization.service';
import { UtilService } from 'src/app/common/services/util/util.service';
import { formatDate, isEmpty } from 'src/app/common/utils';
import {
  ETransactionStatus,
  ITransactionHeader,
  PaymentTerm
} from 'src/app/EBMSuite/models/transaction.model';
import { LicenseService } from 'src/app/EBMSuite/services/license/license.service';
import { SalesService } from 'src/app/EBMSuite/services/sales/sales.service';
import { ReceiptsCustomizeComponent } from '../../../ebmsuite-shared/components/receipts-customize/receipts-customize.component';

@Component({
  selector: 'app-all-invoices',
  templateUrl: './all-invoices.component.html',
  styleUrls: ['./all-invoices.component.scss'],
  providers: [DialogService, MessageService, ConfirmationService]
})
export class AllInvoicesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() dashboardView: boolean | null = null;
  @ViewChild('paginator', { static: true }) paginator!: Paginator;
  subscription = new Subscription();
  modalDismissed = false;
  invoicesHeaders: ITransactionHeader[] = [];
  customerSearch: any;
  isLoaded = false;
  tableColumns: {
    displayName: string;
    fieldName: string;
    sortField: string;
    dashboardView: boolean;
  }[] = [
    {
      displayName: 'Transaction code',
      fieldName: 'transactionUniqueCode',
      sortField: 'transactionUniqueCode',
      dashboardView: true
    },

    {
      displayName: 'Customer',
      fieldName: 'customer.name',
      sortField: 'customer',
      dashboardView: false
    },
    {
      displayName: 'Transaction date',
      fieldName: 'transactionDate',
      sortField: 'transactionDate',
      dashboardView: true
    },
    {
      displayName: 'Status',
      fieldName: 'transactionStatus',
      sortField: 'transactionStatus',
      dashboardView: true
    },
    {
      displayName: 'Due Date',
      fieldName: 'transactionDueDate',
      sortField: 'transactionDueDate',
      dashboardView: false
    },
    {
      displayName: 'Total amount',
      fieldName: 'transactionTotalAmount',
      sortField: 'transactionTotalAmount',
      dashboardView: true
    },

    {
      displayName: 'Balance',
      fieldName: 'transactionBalance',
      sortField: 'transactionBalance',
      dashboardView: true
    },
    {
      displayName: 'Created by',
      fieldName: 'createdBy.firstname',
      sortField: 'createdBy.firstname',
      dashboardView: false
    }
  ];
  rows = this.dashboardView ? 10 : 50;
  allRecordsCount = 0;
  defaultPagination: IPagination<ITransactionHeader[]> = {
    page: 0,
    pageSize: this.rows,
    searchParam: '',
    startDate: '',
    endDate: '',
    totalCount: 0,
    data: [],
    sortKey: 'transactionUniqueCode',
    sortDirection: -1
  };
  searchText = '';
  initialSearch = '';
  allInvoicesCount = 0;
  exportingItems = false;
  invoiceCustomizeModal = false;
  displayGuide = false;
  paymentDialog = false;

  customizeInvoiceForm = new FormGroup({});
  model: any = {};
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [
    {
      key: 'tagline',
      type: 'input',
      props: {
        label: 'Tagline',
        placeholder: 'Enter your organization tagline for the invoice footer'
      }
    },
    {
      key: 'privacyPolicy',
      type: 'editor',
      props: {
        label: 'privacyPolicy'
      }
    }
  ];
  currentOrganization!: IOrganization;
  organizationPaymentTerms: any[] = [];
  defaultPaymentMethods = Object.keys(PaymentTerm);

  remainingTransactions!: any;

  items: MenuItem[] = [
    {
      label: 'Date',
      items: [
        {
          label: 'Today',
          icon: 'pi pi-calendar',
          command: () => {
            this.filterByToday();
          }
        },
        {
          label: 'Select range',
          escape: false,
          icon: 'pi pi-calendar-plus',
          command: () => {
            this.calendarVisible = true;
          }
        }
      ]
    },
    { separator: true },
    {
      label: 'Status',
      items: [
        {
          label: 'Not Paid (Draft)',
          command: () => {
            this.searchText = 'DRAFT';
            this.getSearchResults();
          }
        },
        {
          label: 'Pending',
          command: () => {
            this.searchText = 'PENDING';
            this.getSearchResults();
          }
        },
        {
          label: 'Partially Paid',
          command: () => {
            this.searchText = 'PARTIALLYPAID';
            this.getSearchResults();
          }
        },
        {
          label: 'Paid',
          command: () => {
            this.searchText = 'PAID';
            this.getSearchResults();
          }
        }
      ]
    }
  ];

  calendarVisible = false;
  dateRangeFilter: Date[] = [];

  addPaymentForm = new FormGroup({
    paidInCurrencyAmount: new FormControl(0, [Validators.required]),
    paidInCurrency: new FormControl({ value: '', disabled: true }, [Validators.required]),
    paymentTerm: new FormControl('', [Validators.required])
  });

  formattedCurrencyList: any[] = [];
  selectedTransaction!: ITransactionHeader;

  currentDate = new Date();

  invoiceHistory: any;
  invoiceHistoryDialog = false;

  router = inject(Router);
  salesService = inject(SalesService);
  utilService = inject(UtilService);
  dialogService = inject(DialogService);
  organizationService = inject(OrganizationService);
  messageService = inject(MessageService);
  licenseService = inject(LicenseService);

  ngOnInit(): void {
    this.isLoaded = false;
    this.getCurrentOrganization();
    if (!this.dashboardView) {
      // this.getAllInvoicesCount();
      this.getPaginatedInvoices();
    }
    this.dashboardViewSettings();
    this.getRemainingTransactionStarterPlan();
  }

  ngOnChanges(changes: SimpleChanges) {
    const dashboardViewChanges = changes['dashboardView'];
    if (dashboardViewChanges) {
      const newValue = dashboardViewChanges.currentValue;
      if (newValue != null) {
        this.rows = newValue === true ? 10 : 50;
        this.defaultPagination = {
          ...this.defaultPagination,
          pageSize: newValue === true ? 10 : 50
        };
        this.getAllInvoicesCount();
        this.getPaginatedInvoices();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  dashboardViewSettings(): void {
    if (this.dashboardView) {
      this.tableColumns = this.tableColumns.filter((x) => x.dashboardView);
    }
  }

  getInvoices(): void {
    this.exportingItems = true;

    const keys = new Map([
      ['transactionUniqueCode', 'Invoice Number'],
      ['transactionDate', 'Invoice Date'],
      ['transactionDate', 'Invoice Due Date'],
      ['customer', 'Billed To'],
      ['createdBy', 'Invoice Created By'],
      ['convertedTransactionTotalCostAmount', 'Cost of Goods'],
      ['convertedOriginalNonDiscountAmount', 'Subtotal'],
      ['convertedDiscountTotal', 'Discount Applied'],
      ['convertedTransactionTotalAmount', 'Net Revenue (excl. tax)'],
      ['convertedTaxTotal', 'Total Taxes Applied'],
      ['convertedAmount', 'Net Revenue (incl. tax)'],
      ['transactionTotalProfit', 'Total Gross Profit'],
      ['transactionPaidTotal', 'Amount Paid'],
      ['transactionStatus', 'Invoice Status'],
      ['updatedAt', 'Invoice Last Updated On']
    ]);
    this.salesService
      .getPaginatedInvoices(this.defaultPagination)
      .pipe(
        map((res) => res.result.data ?? []),
        map((data) => {
          const result = [];
          for (const item of data) {
            const object: any = {};
            keys.forEach((v, k) => {
              const value = item[k as keyof ITransactionHeader];

              if (k === 'createdBy') {
                object[v] = `${
                  (value as unknown as IUser).firstname
                } ${(value as unknown as IUser).lastname}`;
              } else if (k === 'customer') {
                object[v] = !isEmpty(value) ? (value as unknown as IContact).name : '';
              } else if (k === 'transactionStatus') {
                object[v] =
                  value === ETransactionStatus.PARTIALLYPAID ? 'PARTIALLY PAID' : value;
              } else if (
                k === 'transactionDate' ||
                k === 'transactionDueDate' ||
                k === 'updatedAt'
              ) {
                object[v] = formatDate(new Date(value as number));
              } else if (typeof value === 'number') {
                object[v] = (value as number).toFixed(2);
              } else {
                object[v] = value;
              }
            });
            result.push(object);
          }
          return result;
        })
      )
      .subscribe({
        next: (data) => {
          this.utilService.exportAsExcelFile(data, 'invoices');
          this.exportingItems = false;
        }
      });
  }

  getPaginatedSortedInvoices(e: SortEvent) {
    const order = e.order;
    let field = e.field;
    if (field!.includes('.')) {
      field = field!.split('.')[0];
    }

    if (
      field === this.defaultPagination.sortKey &&
      order === this.defaultPagination.sortDirection
    )
      return;

    this.defaultPagination = {
      ...this.defaultPagination,
      sortKey: field,
      sortDirection: order
    };
    this.getPaginatedInvoices();
  }

  getPaginatedInvoices(): void {
    this.subscription.add(
      this.salesService.getPaginatedInvoices(this.defaultPagination).subscribe({
        next: (res) => {
          const invoicesHeaders = res.result;

          this.defaultPagination = {
            ...this.defaultPagination,
            ...invoicesHeaders
          };

          this.invoicesHeaders =
            invoicesHeaders?.data.length > 0
              ? invoicesHeaders.data.map((x: any) => this.appendDropdownItemsToItem(x))
              : [];
        }
      })
    );
  }

  paginate(event: any): void {
    this.defaultPagination = {
      ...this.defaultPagination,
      page: event.page
    };
    this.getPaginatedInvoices();
  }

  getAllInvoicesCount(): void {
    this.subscription.add(
      this.salesService.getAllInvoicesCount().subscribe({
        next: (res) => {
          this.allInvoicesCount = res.result;
        }
      })
    );
  }

  getAllSearchedInvoicesCount(): void {
    this.subscription.add(
      this.salesService.getAllSearchedInvoicesCount(this.searchText).subscribe({
        next: (res) => {
          this.allInvoicesCount = res.result;
        }
      })
    );
  }
  searchOnEnter(searchInput: string) {
    this.searchText = searchInput;
    this.getSearchResults();
  }

  getSearchResults(): void {
    setTimeout(() => this.paginator.changePage(0));
    if (this.searchText) {
      if (this.initialSearch?.toLowerCase() !== this.searchText?.toLowerCase()) {
        this.initialSearch = this.searchText;
        this.defaultPagination = {
          ...this.defaultPagination,
          page: 0,
          searchParam: this.searchText
        };
      } else {
        this.defaultPagination = {
          ...this.defaultPagination,
          searchParam: this.searchText
        };
      }
    } else {
      this.defaultPagination = {
        ...this.defaultPagination,
        page: 0,
        searchParam: ''
      };
    }
    this.getPaginatedInvoices();
  }

  filterByDate() {
    this.defaultPagination = {
      ...this.defaultPagination,
      page: 0,
      pageSize: this.rows,
      startDate: this.dateRangeFilter[0].toDateString(),
      endDate: this.dateRangeFilter[1]
        ? this.dateRangeFilter[1].toDateString()
        : new Date(this.dateRangeFilter[0].getTime() + 24 * 60 * 60 * 1000).toDateString()
    };
    this.getPaginatedInvoices();
    this.calendarVisible = false;
  }

  filterByToday() {
    this.defaultPagination = {
      ...this.defaultPagination,
      page: 0,
      pageSize: this.rows,
      startDate: new Date(new Date().setHours(0, 0, 0, 0)).toDateString(),
      endDate: new Date(
        new Date(new Date().setHours(0, 0, 0, 0)).getFullYear() + 100,
        new Date(new Date().setHours(0, 0, 0, 0)).getMonth(),
        new Date(new Date().setHours(0, 0, 0, 0)).getDate()
      ).toDateString()
    };
    this.getPaginatedInvoices();
  }

  clearSearch() {
    this.dateRangeFilter = [];
    this.searchText = '';
    this.defaultPagination = {
      ...this.defaultPagination,
      startDate: null,
      endDate: null
    };
    this.getSearchResults();
  }

  exportInvoices(invoices: any[]): void {
    this.utilService.exportAsExcelFile(invoices, 'All-Invoices');
  }

  public openDeleteConfirmDialog(item: any) {
    const ref = this.dialogService.open(DeleteConfirmDialogComponent, {
      header: 'Delete Confirmation',
      width: '50%',
      baseZIndex: 10000
    });
    ref.onClose.subscribe((confirm) => {
      if (confirm) {
        this.deleteInvoice(item?._id);
      }
    });
  }
  deleteInvoice(invoiceId: string): void {
    this.subscription.add(
      this.salesService.deleteInvoice(invoiceId).subscribe({
        complete: () => this.getPaginatedInvoices()
      })
    );
  }

  appendDropdownItemsToItem(transactionHeader: ITransactionHeader): any {
    const tableActionButtons = [
      {
        label: 'Add payment',
        icon: 'pi pi-money-bill',
        command: () => {
          this.selectedTransaction = transactionHeader;
          this.openPaymentDialog();
        }
      },
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        routerLink: ['../edit-invoice/' + transactionHeader?._id]
      },
      {
        label: 'History',
        icon: 'pi pi-history',
        command: () => this.showInvoiceHistoryDialog(transactionHeader._id!)
      },
      {
        label: 'Duplicate',
        icon: 'pi pi-copy',
        routerLink: ['../duplicate-invoice/' + transactionHeader?._id]
      },
      {
        label: 'Cancel invoice',
        icon: 'pi pi-times-circle',
        command: () => this.cancelTransaction(transactionHeader)
      },
      {
        label: 'Delete',
        icon: 'pi pi-times',
        command: () => this.openDeleteConfirmDialog(transactionHeader)
      }
    ];
    if (transactionHeader.transactionStatus === 'DRAFT') {
      tableActionButtons.splice(0, 1);
    }
    if (transactionHeader) {
      transactionHeader.menuItems = tableActionButtons;
    }
    return transactionHeader;
  }

  getCurrentOrganization(): void {
    this.subscription.add(
      this.organizationService.getCurrentOrganization().subscribe((res) => {
        this.currentOrganization = res.result.organization;
        this.model = this.currentOrganization?.printoutSettings?.invoice;
      })
    );
  }

  customizeOrUpdateInvoice(form: any): void {
    const organization = {
      ...this.currentOrganization,
      printoutSettings: {
        ...this.currentOrganization?.printoutSettings,
        invoice: form
      }
    };
    this.organizationService.editOrganization(organization).subscribe({
      complete: () => {
        this.customizeInvoiceForm.reset();
        this.invoiceCustomizeModal = false;
      }
    });
  }

  public openCustomizeInvoiceDialog() {
    const ref = this.dialogService.open(ReceiptsCustomizeComponent, {
      header: 'Customize Invoice',
      width: '50%',
      baseZIndex: 10000,
      data: this.currentOrganization?.printoutSettings?.invoice
    });
    ref.onClose.subscribe((customizeData) => {
      if (customizeData) {
        this.customizeOrUpdateInvoice(customizeData);
      }
    });
  }

  openPaymentDialog() {
    this.paymentDialog = true;
    this.getOrganizationPaymentMethods();
    this.getCurrency();

    const selectedInvoiceData = {
      paidInCurrencyAmount: this.selectedTransaction.transactionBalance,
      paidInCurrency: this.selectedTransaction?.currency
        ? this.selectedTransaction.currency.symbol
          ? this.selectedTransaction.currency.symbol
          : this.selectedTransaction.currency.code
        : this.utilService.getCurrentCurrency(),
      paymentTerm: ''
    };

    this.addPaymentForm.patchValue(selectedInvoiceData);
  }

  getOrganizationPaymentMethods(): void {
    this.subscription.add(
      this.organizationService.getCurrentOrganization().subscribe({
        next: (res) => {
          if (res.result?.organization?.paymentTerms?.length) {
            this.organizationPaymentTerms =
              res.result?.organization?.paymentTerms?.length > 0
                ? res.result.organization.paymentTerms
                : this.defaultPaymentMethods;
          }
        }
      })
    );
  }
  getCurrency(): void {
    this.subscription.add(
      this.organizationService.getCurrencies().subscribe({
        next: (res) => {
          const currencyList = res.result;
          this.formattedCurrencyList = currencyList.map((currency) => ({
            ...currency,
            formattedLabel: `${currency.country} (${currency.code})`,
            currencySymbolOrCode: {
              symbol: currency.symbol ? currency.symbol : null,
              code: currency.code
            }
          }));
        }
      })
    );
  }

  addPayment(): void {
    const paymentRecord = {
      ...this.addPaymentForm.value,
      transaction: this.selectedTransaction._id,
      paidInCurrency: this.selectedTransaction?.currency?.code,
      amount: this.selectedTransaction?.currency?.code
    };
    this.subscription.add(
      this.salesService.appendTransactionPaymentRecord(paymentRecord).subscribe({
        next: (res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res.message
          });
          this.paymentDialog = false;
        },
        error: (err) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: err.message
          });
        },
        complete: () => {
          this.getPaginatedInvoices();
        }
      })
    );
  }

  calculateDifferenceInDays(transactionDueDate: Date): number {
    const currentDate = new Date().setHours(0, 0, 0, 0);
    const timeDifference = new Date(transactionDueDate).getTime() - currentDate;
    return Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  }

  displayDueDate(transactionDueDate: Date): string {
    const differenceInDays = this.calculateDifferenceInDays(transactionDueDate);

    if (differenceInDays === 0) {
      return 'Today';
    } else if (differenceInDays > 0) {
      return `In ${differenceInDays} day${differenceInDays > 1 ? 's' : ''}`;
    } else {
      return `${-differenceInDays} day${differenceInDays < -1 ? 's' : ''} ago`;
    }
  }

  getRemainingTransactionStarterPlan(): void {
    this.subscription.add(
      this.licenseService.getRemainingTransactionStarterPlan().subscribe({
        next: (res) => {
          this.remainingTransactions = res.result;
        }
      })
    );
  }

  showInvoiceHistoryDialog(invoiceId: string) {
    this.invoiceHistoryDialog = true;
    this.getInvoiceHistory(invoiceId);
  }

  getInvoiceHistory(invoiceId: string): void {
    this.subscription.add(
      this.salesService.getInvoiceHistory(invoiceId).subscribe((res) => {
        this.invoiceHistory = res.result;
      })
    );
  }

  cancelTransaction(transactionHeader: ITransactionHeader): void {
    const transaction = {
      ...transactionHeader,
      transactionStatus: ETransactionStatus.CANCELLED
    };
    this.subscription.add(
      this.salesService.updateSale(transaction).subscribe({
        complete: () => {
          this.getPaginatedInvoices();
        }
      })
    );
  }
}
